import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { getActiveAutoSyncForPill } from '../../utils/localFolderSync/autoSyncRegistry';

const POLL_MS = 15000;

// Floating "auto-sync is running" pill, fixed to the right edge of the
// viewport - a reminder + quick way back for a user who navigated away from
// wherever they armed auto-sync (an organiser's own EventPage, or a guest's
// upload-link page) to somewhere with no other clue it's still watching in
// the background. Reads IndexedDB directly (via autoSyncRegistry) rather
// than a live useLocalAutoSync instance - neither Landing nor Dashboard (the
// two places this mounts) has one, and the armed auto-sync could belong to a
// completely different event/link than anything on screen here.
export default function ActiveAutoSyncPill() {
  const [autoSync, setAutoSync] = useState(null);
  const navigate = useNavigate();

  const refresh = useCallback(() => {
    // getActiveAutoSyncForPill live-verifies permission against the persisted
    // dirHandle on every call, rather than trusting the record's own
    // needsReconnect flag alone - that flag only gets refreshed by a live
    // useLocalAutoSync poll/mount-check, which never runs on Landing/
    // Dashboard (where this pill lives), so a permission lost while the user
    // never revisited the page that armed it would otherwise leave this pill
    // stuck showing "running" forever.
    getActiveAutoSyncForPill().then(setAutoSync).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [refresh]);

  if (!autoSync) return null;

  const handleClick = () => {
    navigate(autoSync.isUploadLink ? `/upload/${autoSync.token}` : `/event/${autoSync.eventId}`);
  };

  return (
    <Box
      component="button"
      type="button"
      onClick={handleClick}
      aria-label="Auto-sync is running - tap to open"
      sx={{
        position: 'fixed',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 1.25,
        pl: 1.75,
        pr: 2,
        border: 'none',
        borderRadius: '999px 0 0 999px',
        background: 'linear-gradient(135deg, #C73866 0%, #e0698f 100%)',
        color: '#fff',
        fontFamily: 'inherit',
        fontSize: '0.85rem',
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: '0 4px 16px rgba(199, 56, 102, 0.35)',
        '&:hover': { filter: 'brightness(1.05)' },
      }}
    >
      <SyncIcon fontSize="small" />
      Auto-sync running
    </Box>
  );
}
